import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import {
  useRecoilValue, useRecoilValueLoadable
} from 'recoil';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  InputNumber,
  Upload,
  Image,
  Modal,
  DatePicker,
  Descriptions,
  Input,
  Table,
  Switch,
  Avatar,
  Tooltip,
  Select,
  notification,
  Pagination,
  Button
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import {
  InboxOutlined,
  FormOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import {
  timezoneAtom,
  businessAtom,
  profileAtom,
  rowsAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import {
  convertTaxRule,
  booleanConvert,
  thousand,
  convertPriceMethod,
  thousandv2,
  toCurrency
} from '../../utils/functions';

function CurrentShopper() {
  const { t, i18n } = useTranslation();
  const business = useRecoilValue(businessAtom);
  const timezone = useRecoilValue(timezoneAtom);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getShopper() {
    setLoading(true);
    api
      .post(`pos/prices/current/promo/${business?.id}`, null)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setProducts(response.data.data.results);
          setFilteredProducts(response.data.data.results);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  }

  useEffect(() => {
    getShopper();
  }, [business]);

  useEffect(() => {
    setFilteredProducts(_.filter(products, (p) => `${p.completeName} ${p.completeNameSpa} ${p.itemNumber} ${p.gtin} ${p.sectionNameEs} ${p.sectionNameEn} ${p.superSectionNameEs} ${p.superSectionNameEn}`
      .replace('              ', '').toLocaleLowerCase().includes(searchText.toLocaleLowerCase())));
  }, [searchText]);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={`${t('current_shopper')}: ${thousand(products?.length)}`}
      />
      <div>
        <div>
          <Input
            placeholder={t('item_price_search_general')}
            value={searchText}
            onChange={(obj) => setSearchText(obj.target.value)}
          />
          <div className="mx-2">{t('results')}: {filteredProducts?.length}</div>
          <Table
            bordered
            loading={loading}
            dataSource={filteredProducts}
            rowKey="id"
            columns={[
              {
                title: t('image'),
                key: 'image',
                align: 'center',
                className: 'text-xs',
                render: (row) => (
                  <Image
                    width={50}
                    src={row.imageUrl}
                    alt={`${row.brand} ${row.name} ${row.description}`}
                  />
                ),
              },
              {
                title: t('UPC'),
                key: 'upc',
                align: 'center',
                className: 'text-xs',
                sorter: (a, b) => a.upc.localeCompare(b.upc),
                render: (row) => <span>{row.upc}</span>,
              },
              {
                title: t('name'),
                key: 'name',
                align: 'left',
                className: 'text-xs',
                sorter: (a, b) => (i18n.language === 'en' ? a.completeName.localeCompare(b.completeName) : a.completeNameSpa.localeCompare(b.completeNameSpa)),
                render: (row) => <span>{i18n.language === 'en' ? row.completeName : row.completeNameSpa}</span>,
              },
              {
                title: t('department'),
                key: 'department',
                align: 'left',
                className: 'text-xs',
                sorter: (a, b) => (i18n.language === 'en' ? a.superSectionNameEn.localeCompare(b.superSectionNameEn) : a.superSectionNameEs.localeCompare(b.superSectionNameEs)),
                render: (row) => <span>{i18n.language === 'en' ? row.superSectionNameEn : row.superSectionNameEs}</span>,
              },
              {
                title: t('category'),
                key: 'category',
                align: 'left',
                className: 'text-xs',
                sorter: (a, b) => (i18n.language === 'en' ? a.sectionNameEn.localeCompare(b.sectionNameEn) : a.sectionNameEs.localeCompare(b.sectionNameEs)),
                render: (row) => <span>{i18n.language === 'en' ? row.sectionNameEn : row.sectionNameEs}</span>,
              },
              {
                title: t('promo_price'),
                key: 'promo_price',
                align: 'center',
                className: 'text-xs',
                sorter: (a, b) => a.activePrice <= b.activePrice,
                render: (row) => <span>{toCurrency(row.activePrice)}</span>,
              },
              {
                title: t('base_price'),
                key: 'base_price',
                align: 'center',
                className: 'text-xs',
                sorter: (a, b) => a.regularPrice <= b.regularPrice,
                render: (row) => <span>{toCurrency(row.regularPrice)}</span>,
              },
            ]}
          />
        </div>
      </div>
    </>
  );
}
export default CurrentShopper;
